import { NavigationPage } from '@/app/components'
import LabeledData from '@/app/components/LabeledData/LabeledData'
import { useProfileInfo } from '@/hooks/useProfileId'
import { utilityAccountsCollection } from '@/reducers/utilityAccounts'
import { LoadingAnimation, Text } from '@/components'
import React from 'react'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import { Clipboard, Layers, Zap } from 'react-feather'
import { useNavigation } from '@/app/hooks'
import { match, P } from 'ts-pattern'
import { MissingContentSectionPlan } from '@/app/components/MissingContentSection/MissingContentSection'
import PendingPlanAlert from 'features/connect-utility/UtilityOnboarding/PendingPlanAlert'
import useUtilityPrograms from '@/app/hooks/useUtilityPrograms'
import { AlertMessage } from 'app-components'
import { Plan } from '@/types'
import { useAccountFieldCmsOverrides } from '@/app/features/utility-account-fields/useAccountFieldCmsOverrides'

export enum PlanDisplayType {
  Plan = 'Plan',
  Pending = 'Pending',
  Missing = 'Missing',
}

export function getPlanDisplayType(plan: Plan | null, isPending: boolean) {
  return match({ plan, isPending })
    .with({ plan: P.nonNullable, isPending: false }, () => PlanDisplayType.Plan)
    .with(
      { plan: P.nonNullable, isPending: true },
      () => PlanDisplayType.Pending,
    )
    .with({ plan: P.nullish, isPending: P.any }, () => PlanDisplayType.Missing)
    .exhaustive() as PlanDisplayType
}

export default function ManageUtilityInformationPage() {
  const { utility, plan, home, isLoading: fetchingProfile } = useProfileInfo()
  const { isLoadingOrIdle: fetchingUtility, data: utilityAccountData } =
    utilityAccountsCollection.useFetch('/utilities/utility_account')
  const contractNumber = utilityAccountData?.[0]?.contract_number
  const meterNumber = utilityAccountData?.[0]?.meter_number
  const accountFieldLabels = useAccountFieldCmsOverrides()

  const { enrolledPrograms, isLoading: fetchingPrograms } = useUtilityPrograms({
    onlyMultiPrograms: false,
  })

  const navigate = useNavigation()

  const pendingPlanContent = PendingPlanAlert()
  const planDisplayType = getPlanDisplayType(plan, Boolean(pendingPlanContent))

  const isLoading = fetchingUtility || fetchingProfile

  const enrolledProgramNames = enrolledPrograms
    ? enrolledPrograms.map(
        (program) =>
          program?.view_config_json?.name ??
          program?.view_config_json?.scheduler?.title ??
          program.name,
      )
    : []

  const editUtilityClicked = () => {
    if (!home) {
      navigate.push('/connect-utility/address')
      return
    }

    navigate.push('/connect-utility/select')
  }

  const editAccountNumberClicked = () => {
    navigate.push('/connect-utility/account-number')
  }

  const onBackClicked = () => {
    navigate.push('/settings')
  }

  const loadingContent = (
    <div className="flex flex-col items-center">
      <LoadingAnimation type="falling" centered />{' '}
      <Text variant="body1" className="mt-2">
        Loading utility information...
      </Text>
    </div>
  )

  const planEnrollmentWarning = (
    <AlertMessage show={!fetchingPrograms} variant="info" className="mb-6">
      <div className="flex flex-col gap-4">
        <div>
          Editing your utility information may affect your eligibility for
          programs and your current program enrollments.
        </div>
        {Boolean(enrolledProgramNames.length) && (
          <ol className="list-decimal list-inside font-bold">
            {enrolledProgramNames.map((program, index) => {
              return <li key={index}>{program}</li>
            })}
          </ol>
        )}
      </div>
    </AlertMessage>
  )

  const loadedContent = (
    <>
      {planEnrollmentWarning}
      <div className="mb-8">
        <Text variant="subheader" className="mb-2">
          Utility &amp; Rate Plan
        </Text>
        <div className="flex flex-col gap-4">
          <LabeledData
            icon={<Zap size={20} />}
            title="Utility Name"
            subtitle={utility?.name ?? 'Not Set'}
            buttonProps={{
              onClick: editUtilityClicked,
              variant: 'primary',
              text: 'Edit',
            }}
          />
          {match(planDisplayType)
            .with(PlanDisplayType.Plan, () => (
              <LabeledData
                icon={<Layers size={20} />}
                title="Plan Name"
                subtitle={plan?.name ?? 'Not Set'}
              />
            ))
            .with(PlanDisplayType.Pending, () => <PendingPlanAlert />)
            .with(PlanDisplayType.Missing, () => <MissingContentSectionPlan />)
            .exhaustive()}
        </div>
      </div>
      <div className="mb-8">
        <Text variant="subheader" className="mb-2">
          Account Details
        </Text>
        <div className="flex flex-col gap-4">
          <LabeledData
            icon={<Clipboard size={20} />}
            title={accountFieldLabels.account_number.label}
            subtitle={utilityAccountData?.[0]?.account_number ?? 'Not provided'}
            buttonProps={{
              onClick: editAccountNumberClicked,
              variant: 'primary',
              text: 'Edit',
            }}
          />
          {contractNumber && (
            <LabeledData
              icon={<Clipboard size={20} />}
              title={accountFieldLabels.contract_number.label}
              subtitle={contractNumber}
            />
          )}
          {meterNumber && (
            <LabeledData
              icon={<Clipboard size={20} />}
              title={accountFieldLabels.meter_number.label}
              subtitle={meterNumber}
            />
          )}
        </div>
      </div>
    </>
  )

  return (
    <NavigationPage
      id="manage-utility-information"
      title="Manage Utility Information"
      backOverride={onBackClicked}
    >
      <NavigationPage.SingleCardContent>
        <LoadingContainer
          isLoading={isLoading}
          loadedContent={loadedContent}
          loadingContent={loadingContent}
        />
      </NavigationPage.SingleCardContent>
    </NavigationPage>
  )
}
